// src/components/admin/DoctorAccountView.tsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../services/axiosInstance";
import { API_ENDPOINTS } from "../../services/api/apiConfig";
import Header from "../common/Header";
import LoadingScreen from "../common/LoadingScreen";
import ResponseMessage from "../common/ResponseMessage";
import { ArrowLeft } from "lucide-react";
import type { Doctor } from "../../types";

const DoctorAccountView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [doctor, setDoctor] = useState<Doctor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDoctor = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No token found");
        const response = await axiosInstance.get(
          API_ENDPOINTS.USERS.DOCTORS.GET_BY_ID(Number(id))
        );
        setDoctor(response.data);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError("Erreur lors de la récupération des détails du docteur");
        setLoading(false);
      }
    };
    if (id) fetchDoctor();
  }, [id]);

  if (loading)
    return (
      <LoadingScreen
        message="Chargement des détails du docteur..."
        role="admin"
      />
    );
  if (error || !doctor)
    return (
      <ResponseMessage message={error || "Docteur non trouvé"} role="admin" />
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4">
      <Header
        title="Détails du Docteur"
        description="Visualisez les informations du compte docteur."
        action={{
          label: "Retour",
          to: "/admin/dashboard/account/doctor",
          icon: <ArrowLeft className="w-4 h-4" />,
        }}
        role="admin"
      />
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 max-w-2xl mx-auto">
        <div className="grid grid-cols-1 gap-4">
          <div>
            <strong>Nom:</strong> {doctor.firstName}
          </div>
          <div>
            <strong>Prénom:</strong> {doctor.lastName}
          </div>
          <div>
            <strong>Nom d'utilisateur:</strong> {doctor.username}
          </div>
          <div>
            <strong>Date de naissance:</strong>{" "}
            {doctor.dateOfBirth
              ? new Date(doctor.dateOfBirth).toLocaleDateString("fr-FR")
              : ""}
          </div>
          <div>
            <strong>Spécialité:</strong> {doctor.specialty}
          </div>
          <div>
            <strong>Genre:</strong>{" "}
            {doctor.gender === "M"
              ? "Masculin"
              : doctor.gender === "F"
                ? "Féminin"
                : "Autre"}
          </div>
          <div>
            <strong>Adresse:</strong> {doctor.address}
          </div>
          <div>
            <strong>Téléphone:</strong> {doctor.phone}
          </div>
          <div>
            <strong>Email:</strong> {doctor.email}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorAccountView;
